import React, { useState, useEffect, useRef } from 'react';
import { io } from 'socket.io-client';
import api from '../api';

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || (window.location.protocol + '//' + window.location.hostname + (window.location.port ? ':4000' : ''));

function Chat({ contextType, contextId, contextDate }) {
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState('name'); // name -> connecting -> live | offline
  const [name, setName] = useState('');
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [conversationId, setConversationId] = useState(null);
  const [ownerOnline, setOwnerOnline] = useState(false);
  const [typing, setTyping] = useState(false);
  const [waitSeconds, setWaitSeconds] = useState(60);
  const socketRef = useRef(null);
  const messagesEndRef = useRef(null);
  const visitorId = useRef(localStorage.getItem('diary_visitor_id') || generateId());

  useEffect(() => {
    localStorage.setItem('diary_visitor_id', visitorId.current);
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const startChat = async () => {
    if (!name.trim()) return;
    setStep('connecting');

    // Connect socket and wait up to 60s for host to come online
    const socket = io(SOCKET_URL, { transports: ['websocket', 'polling'] });
    socketRef.current = socket;

    let resolved = false;

    const goLive = () => {
      if (resolved) return;
      resolved = true;
      setOwnerOnline(true);
      setStep('live');
    };

    socket.on('connect', () => {
      socket.emit('check_owner_status');
    });

    socket.on('owner_status', (data) => {
      setOwnerOnline(data.online);
      if (data.online) {
        goLive();
      }
    });

    // Countdown timer for UI
    setWaitSeconds(60);
    const countdownInterval = setInterval(() => {
      setWaitSeconds(prev => {
        if (prev <= 1) { clearInterval(countdownInterval); return 0; }
        return prev - 1;
      });
    }, 1000);

    // Keep checking every 5 seconds
    const interval = setInterval(() => {
      if (!resolved) {
        socket.emit('check_owner_status');
      }
    }, 5000);

    // After 60 seconds, if still not online, show offline
    const timeout = setTimeout(() => {
      if (!resolved) {
        resolved = true;
        clearInterval(interval);
        clearInterval(countdownInterval);
        setStep('offline');
      }
    }, 60000);

    // If owner comes online at any point during the wait
    socket.on('owner_status', (data) => {
      setOwnerOnline(data.online);
      if (data.online && !resolved) {
        resolved = true;
        clearInterval(interval);
        clearInterval(countdownInterval);
        clearTimeout(timeout);
        setStep('live');
      }
    });

    socket.on('new_message', (msg) => {
      setMessages(prev => [...prev, msg]);
    });

    socket.on('user_typing', () => setTyping(true));
    socket.on('user_stop_typing', () => setTyping(false));

    // Cleanup on unmount
    socket._chatCleanup = () => {
      clearInterval(interval);
      clearInterval(countdownInterval);
      clearTimeout(timeout);
    };
  };

  const beginConversation = async (mode) => {
    // mode: 'live' or 'message'
    try {
      const { data } = await api.post('/chat/conversations', {
        visitor_name: name,
        visitor_id: visitorId.current,
        context_type: contextType || null,
        context_id: contextId || null,
        context_date: contextDate || null
      });
      setConversationId(data.conversation.id);
      socketRef.current?.emit('join_conversation', data.conversation.id);

      const msgRes = await api.get(`/chat/conversations/${data.conversation.id}/messages`);
      setMessages(msgRes.data.messages);

      setStep(mode === 'live' ? 'chatting' : 'leaving_message');
    } catch (err) {
      console.error('Failed to start chat:', err);
    }
  };

  const sendMessage = () => {
    if (!input.trim() || !conversationId) return;

    socketRef.current?.emit('send_message', {
      conversation_id: conversationId,
      sender_type: 'visitor',
      sender_name: name,
      content: input.trim()
    });

    setInput('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const closeChat = () => {
    setIsOpen(false);
    if (socketRef.current) {
      if (socketRef.current._chatCleanup) socketRef.current._chatCleanup();
      socketRef.current.disconnect();
      socketRef.current = null;
    }
    setStep('name');
    setMessages([]);
    setConversationId(null);
  };

  return (
    <>
      {/* Chat CTA Banner */}
      {!isOpen && (
        <div className="chat-banner" onClick={() => setIsOpen(true)}>
          <div className="chat-banner-dot"></div>
          <span>want to talk about this? start a conversation</span>
        </div>
      )}

      {/* Floating trigger */}
      <button className="chat-trigger" onClick={() => isOpen ? closeChat() : setIsOpen(true)}>
        {isOpen ? '\u00D7' : '\u2709'}
      </button>

      {/* Chat Panel */}
      {isOpen && (
        <div className="chat-panel">
          <div className="chat-header">
            <div>
              <div className="chat-header-title">let's talk</div>
              <div className="chat-header-sub">
                {contextDate ? `about ${contextDate}` : contextType === 'topic' ? 'about this thought' : 'open conversation'}
              </div>
            </div>
            <button className="btn btn-ghost" onClick={closeChat} style={{ fontSize: '1.2rem', padding: '4px 8px' }}>&times;</button>
          </div>

          {/* Step: Enter Name */}
          {step === 'name' && (
            <div className="chat-step">
              <div className="chat-step-icon">&#9998;</div>
              <p className="chat-step-title">before we talk</p>
              <p className="chat-step-desc">what should I call you?</p>
              <input
                type="text"
                className="chat-name-field"
                placeholder="your name or stay anonymous"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && startChat()}
              />
              <button className="btn btn-primary btn-full" onClick={startChat}>
                start chat
              </button>
            </div>
          )}

          {/* Step: Connecting */}
          {step === 'connecting' && (
            <div className="chat-step">
              <div className="chat-connecting">
                <div className="connecting-dots">
                  <span></span><span></span><span></span>
                </div>
                <p className="chat-step-title">looking for the host</p>
                <p className="chat-step-desc">trying to establish a live connection...</p>
                <div className="connecting-status">
                  <div className="status-line active">socket connection established</div>
                  <div className={`status-line ${waitSeconds < 55 ? 'active' : ''}`}>searching for live presence</div>
                  <div className={`status-line ${waitSeconds < 50 ? 'active' : ''}`}>waiting for host to respond</div>
                </div>
                <div className="connecting-timer">
                  waiting {waitSeconds}s before giving up
                </div>
                <button className="btn btn-ghost" onClick={() => setStep('offline')} style={{ marginTop: '12px', fontSize: '0.78rem' }}>
                  skip wait &middot; leave a message instead
                </button>
              </div>
            </div>
          )}

          {/* Step: Owner is LIVE */}
          {step === 'live' && (
            <div className="chat-step">
              <div className="chat-live-badge">
                <span className="live-dot"></span> LIVE
              </div>
              <p className="chat-step-title">host is online right now</p>
              <p className="chat-step-desc">you can have a real-time conversation</p>
              <button className="btn btn-primary btn-full" onClick={() => beginConversation('live')}>
                start live chat
              </button>
            </div>
          )}

          {/* Step: Owner is OFFLINE */}
          {step === 'offline' && (
            <div className="chat-step">
              <div className="chat-offline-badge">offline</div>
              <p className="chat-step-title">host isn't here right now</p>
              <p className="chat-step-desc">but you can leave a message and they'll see it when they're back</p>
              <button className="btn btn-primary btn-full" onClick={() => beginConversation('message')} style={{ marginBottom: '10px' }}>
                leave a message
              </button>
              <p className="chat-step-hint">they usually reply within a few hours</p>
            </div>
          )}

          {/* Step: Chatting (live or leaving message) */}
          {(step === 'chatting' || step === 'leaving_message') && (
            <>
              <div className="chat-status-bar">
                {step === 'chatting' ? (
                  <><span className="live-dot"></span> live conversation</>
                ) : (
                  <><span className="offline-dot"></span> leaving a message</>
                )}
              </div>
              <div className="chat-messages">
                {messages.length === 0 && (
                  <div className="chat-empty-msg">
                    {step === 'chatting'
                      ? "you're connected. say something."
                      : "write your message. they'll read it later."
                    }
                  </div>
                )}
                {messages.map((msg) => (
                  <div key={msg.id} className={`chat-msg ${msg.sender_type === 'visitor' ? 'chat-msg-visitor' : 'chat-msg-owner'}`}>
                    {msg.sender_type === 'owner' && (
                      <div className="chat-msg-name">{msg.sender_name}</div>
                    )}
                    {msg.content}
                  </div>
                ))}
                {typing && <div className="typing-indicator">typing...</div>}
                <div ref={messagesEndRef} />
              </div>

              <div className="chat-input-area">
                <input
                  type="text"
                  className="chat-input"
                  placeholder={step === 'chatting' ? 'say something...' : 'write your message...'}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                />
                <button className="chat-send" onClick={sendMessage}>&uarr;</button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}

function generateId() {
  return 'v_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
}

export default Chat;
